import { useState } from 'react';
import { Stack } from '@mui/material';
import { useParams } from 'react-router';
import { AccessCourse } from './components/access-course';
import { NewSubscription } from './components/new-subscription';
import { SubscriptionCompleted } from './components/subscription-completes';
import { SubscriptionInProgress } from './components/subscription-in-progess';
import { info } from '@/constants';
import type { CourseDetails } from '@/data/catalog/course';
import { useRegistrationShow } from '@/data/courses/registration';
import {
  SubscriptionStatusCodes,
  SubscriptionStatusPendingCodes,
  type Subscription,
} from '@/data/courses/registration/interfaces';
import { InstitutionLogo } from '@/packages/courses/components/institution-logo';
import { useOffer } from '@/packages/courses/pages/details/context/offer';

const getMostRecentSubscription = (subscriptions: Subscription[]) => {
  if (!subscriptions || subscriptions.length === 0) return null;

  return subscriptions.reduce((mostRecentSubscription, subscription) =>
    subscription.subscribed_at > mostRecentSubscription.subscribed_at
      ? subscription
      : mostRecentSubscription
  );
};

export const OfferCard = ({ institution }: CourseDetails) => {
  const { id } = useParams();
  const { subscriptions } = useRegistrationShow(Number(id));
  const { cheapestOffer, isLoading } = useOffer();
  const [requestedNewSubscription, setRequestedNewSubscription] =
    useState(false);

  const isEduplay = institution?.id === info.EduplayId;
  const mostRecentSubscription = subscriptions
    ? getMostRecentSubscription(subscriptions)
    : null;
  const isCompleted =
    mostRecentSubscription &&
    (mostRecentSubscription.status === SubscriptionStatusCodes.Registered ||
      mostRecentSubscription.status === SubscriptionStatusCodes.Completed);
  const isInProgress =
    mostRecentSubscription &&
    SubscriptionStatusPendingCodes.has(mostRecentSubscription.status);

  const showAccessCourse = isCompleted && isEduplay;
  const showSubscriptionCompleted =
    isCompleted && !isEduplay && !requestedNewSubscription;
  const showSubscriptionInProgress = isInProgress;
  const showNewSubscription =
    (!isInProgress && !isCompleted) || requestedNewSubscription;

  return (
    <Stack
      bgcolor="white"
      sx={({ shape }) => ({
        borderRadius: `${shape.borderRadius}px`,
        padding: '1px',
      })}
    >
      {institution && (
        <InstitutionLogo institution={institution} isLoading={isLoading} />
      )}
      <Stack p={3}>
        {showAccessCourse && <AccessCourse offerId={cheapestOffer?.id} />}
        {showSubscriptionCompleted && (
          <SubscriptionCompleted
            onNewSubscription={() => setRequestedNewSubscription(true)}
          />
        )}
        {showSubscriptionInProgress && (
          <SubscriptionInProgress
            offerId={mostRecentSubscription?.offer_id}
            institutionName={institution?.name}
          />
        )}
        {showNewSubscription && <NewSubscription />}
      </Stack>
    </Stack>
  );
};
